import { api, withFreshAccess } from '../api/client'
import { callAgent, parseJsonish } from '../api/aiCall'
import { searchPosts, searchPostsWithImages, type SearchPostHit } from '../api/search'
import {
  appendAiCampaignRun,
  listSeenPostUris,
  listRecentHandleLikes,
  type AiCampaignRow,
  type AiCampaignRunRow,
} from '../cache/db'
import { useAccounts } from '../store/accounts'
import { useAiSettings } from '../store/aiSettings'


/** Build the scoring system prompt. Model outputs quality + lewdness separately;
 *  the gate and formula are applied in code so the model never does math. */
function buildScoringPrompt(photoGate: boolean): string {
  const photoRule = photoGate
    ? `IMPORTANT: If the image is a photorealistic photograph (looks like a real photo, not drawn/painted/rendered art), set quality = 0 and lewdness = 0.

`
    : ``
  return `You are an adult AI-art curator. For each image output two scores:

quality (0-10) — illustration craft, ignoring content:
  0   Photorealistic photo (not art), or completely broken/unviewable.
  1-2 Garbage art: severe anatomy errors, melted faces, heavy AI glitch artifacts.
  3-4 Weak art: noticeable anatomy problems, muddy coloring, amateurish.
  5-6 Competent: decent linework, acceptable anatomy and colors.
  7-8 Good: strong anatomy, appealing style, attractive lighting and composition.
  9-10 Excellent: exceptional, polished, stands out.

lewdness (0-10) — sexual explicitness:
  0  Fully clothed, no sexual context.
  3  Suggestive outfit or pose, nothing exposed.
  5  Partial nudity / softcore.
  7  Explicit nudity, clear sexual context.
  10 Hardcore / maximally explicit.

${photoRule}Return STRICT JSON only, no prose, no code fences:

{ "scores": [{ "postUri": "<uri>", "quality": <0-10>, "lewdness": <0-10>, "reason": "<one sentence: art style, quality assessment, lewdness level>" }] }

Rules:
- Order of scores MUST match the order of postUris given.
- quality and lewdness MUST be numbers 0-10 (decimals ok).
- If image fails to load: quality 0, lewdness 0, reason "could not evaluate".
- NO text outside the JSON object.`
}

interface ScoringResult {
  scores: {
    postUri: string
    quality: number
    lewdness: number
    reason: string
  }[]
}

/** Build the text/poetry scoring system prompt. Model outputs poetryQuality
 *  and originality; gate + formula are applied in code. */
function buildTextScoringPrompt(): string {
  return `You are a literary curator evaluating written posts. For each post output two scores:

poetryQuality (0-10) — craft and clarity of the writing:
  0   Incoherent, spam, or empty.
  1-2 Very poor; barely readable or repetitive filler.
  3-4 Below average; rough grammar or entirely clichéd phrasing.
  5-6 Competent; clear prose, some deliberate craft.
  7-8 Good; evocative, precise language or strong imagery.
  9-10 Excellent; polished, memorable, distinctive voice.

originality (0-10) — freshness and creative distinctiveness:
  0   Plagiarism, bot spam, or completely generic boilerplate.
  3   Familiar theme with no unique angle.
  5   Some fresh phrasing or unexpected perspective.
  7   Distinctive voice or surprising concept throughout.
  10  Completely original; a genuinely memorable turn of phrase or idea.

Return STRICT JSON only, no prose, no code fences:

{ "scores": [{ "postUri": "<uri>", "poetryQuality": <0-10>, "originality": <0-10>, "reason": "<one sentence>" }] }

Rules:
- Order of scores MUST match the order of posts given.
- poetryQuality and originality MUST be numbers 0-10 (decimals ok).
- If the text is empty or spam: poetryQuality 0, originality 0.
- NO text outside the JSON object.`
}

interface TextScoringResult {
  scores: {
    postUri: string
    poetryQuality: number
    originality: number
    reason: string
  }[]
}

/** Extract unique hashtags from a query string, preserving original casing.
 *  Deduplication is case-insensitive so #NSFW and #nsfw collapse to one,
 *  but #AIイラスト is kept intact (not mangled to #aiイラスト). */
function parseTagsFromQuery(query: string): { tags: string[]; rest: string } {
  const seenLower = new Set<string>()
  const tags: string[] = []
  for (const t of query.match(/#\S+/g) ?? []) {
    const low = t.toLowerCase()
    if (!seenLower.has(low)) {
      seenLower.add(low)
      tags.push(t)
    }
  }
  const rest = query.replace(/#\S+/g, '').replace(/\s+/g, ' ').trim()
  return { tags, rest }
}

/** Power set of tags (all non-empty subsets), sorted most-tags-first so
 *  the most specific combos surface first in the aggregated results.
 *  Capped at maxCombos to avoid hammering the search API. */
function tagPowerSet(tags: string[], maxCombos = 16): string[][] {
  const n = Math.min(tags.length, 5)
  const limited = tags.slice(0, n)
  const combos: string[][] = []
  for (let mask = (1 << n) - 1; mask >= 1; mask--) {
    const combo: string[] = []
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) combo.push(limited[i])
    }
    combos.push(combo)
    if (combos.length >= maxCombos) break
  }
  return combos
}

export interface CampaignRunOptions {
  /** Override the campaign's stored dryRun flag — used by the
   *  validator button to force a dry-run on a live campaign. */
  forceDryRun?: boolean
}

export interface CampaignRunResult {
  run: AiCampaignRunRow
  /** Set when the agent's reply couldn't be parsed; the raw text is on
   *  run.rawAgentText so the UI can show it. */
  parseError?: string
}

/**
 * Execute one tick of an AI campaign. Returns the resulting run row
 * (also persisted to db). Used by the scheduler tick and by the
 * validator button in the campaigns page.
 *
 * Steps:
 *   1. Search posts matching campaign.query (latest first) on the
 *      campaign's account
 *   2. Take the first `imagesPerRun` posts that carry images, grab
 *      the first image per post
 *   3. Ship them all to the agent in one vision call
 *   4. Parse `{ scores: [...] }` and map score → action
 *   5. When not dryRun, execute like / repost via createRecord
 *   6. Persist the run row + return it
 */
export async function runAiCampaign(
  campaign: AiCampaignRow,
  opts: CampaignRunOptions = {},
): Promise<CampaignRunResult> {
  const dryRun = opts.forceDryRun ?? campaign.dryRun
  const startedAt = Date.now()
  const runId = `r_${startedAt.toString(36)}_${Math.random().toString(36).slice(2, 8)}`

  const account = useAccounts
    .getState()
    .accounts.find((a) => a.did === campaign.accountDid)
  if (!account) {
    const run: AiCampaignRunRow = {
      id: runId,
      campaignId: campaign.id,
      startedAt,
      finishedAt: Date.now(),
      error: 'campaign account is not signed in on this device',
      dryRun,
      evaluations: [],
    }
    await appendAiCampaignRun(run)
    return { run }
  }

  const agent = useAiSettings
    .getState()
    .agents.find((a) => a.id === campaign.agentId)
  if (!agent) {
    const run: AiCampaignRunRow = {
      id: runId,
      campaignId: campaign.id,
      startedAt,
      finishedAt: Date.now(),
      error: `agent ${campaign.agentId} not found — pick a new one in /settings`,
      dryRun,
      evaluations: [],
    }
    await appendAiCampaignRun(run)
    return { run }
  }

  // Build the matrix of search queries from all tag combinations.
  const { tags, rest } = parseTagsFromQuery(campaign.query)
  const combos = tags.length > 0 ? tagPowerSet(tags) : null
  const searchQueries =
    combos && combos.length > 0
      ? combos.map((c) => [c.join(' '), rest].filter(Boolean).join(' '))
      : [campaign.query]

  // Fetch more posts per combo so there's a larger pool to dedup against.
  // sort:latest always returns newest posts at position 0 — no cursor
  // pagination here because a cursor advances into older content and would
  // skip posts published since the last run. seenPostUris handles dedup.
  const limitPerQuery = Math.max(50, campaign.imagesPerRun * 5)

  const scoringMode = campaign.scoringMode ?? 'image'
  let hits: SearchPostHit[] = []
  let seenUrisBefore = 0
  let rawImageHits = 0
  let passedDedup = 0
  let handleFiltered = 0
  try {
    const searchFn = scoringMode === 'text' ? searchPosts : searchPostsWithImages
    const batches = await Promise.all(
      searchQueries.map(async (q) => {
        try {
          const result = await searchFn({
            accountId: account.id,
            q,
            sort: 'latest',
            limit: limitPerQuery,
          })
          return result.hits
        } catch {
          return [] as SearchPostHit[]
        }
      }),
    )

    // Dedup against every URI this campaign has ever evaluated.
    const seenFromHistory = await listSeenPostUris(campaign.id)
    const seen = new Set<string>([...(campaign.seenPostUris ?? []), ...seenFromHistory])
    seenUrisBefore = seen.size

    // For image mode also dedup by image URL to avoid scoring the same art twice.
    const seenImageUrls = new Set<string>()
    for (const batch of batches) {
      rawImageHits += batch.length
      for (const hit of batch) {
        const imgUrl = hit.images[0]?.fullsize ?? ''
        const imageAlreadySeen = scoringMode === 'image' && imgUrl && seenImageUrls.has(imgUrl)
        if (!seen.has(hit.uri) && !imageAlreadySeen) {
          seen.add(hit.uri)
          if (imgUrl) seenImageUrls.add(imgUrl)
          hits.push(hit)
        }
      }
    }
    passedDedup = hits.length
    // Fisher-Yates shuffle so each run gets a random slice of results.
    for (let i = hits.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[hits[i], hits[j]] = [hits[j], hits[i]]
    }
    // Handle diversity: skip handles that have been liked too recently
    // to prevent the campaign from engaging the same accounts repeatedly.
    const windowRuns = campaign.handleWindowRuns ?? 10
    const maxPerHandle = campaign.maxPostsPerHandle ?? 2
    const recentHandleCounts = await listRecentHandleLikes(campaign.id, windowRuns)
    const runHandleCounts = new Map<string, number>()
    const diverseHits: SearchPostHit[] = []
    for (const hit of hits) {
      const h = hit.authorHandle
      const crossRun = recentHandleCounts.get(h) ?? 0
      const thisRun = runHandleCounts.get(h) ?? 0
      if (crossRun + thisRun >= maxPerHandle) {
        handleFiltered++
        continue
      }
      runHandleCounts.set(h, thisRun + 1)
      diverseHits.push(hit)
    }
    hits = diverseHits.slice(0, campaign.imagesPerRun)
  } catch (e) {
    const run: AiCampaignRunRow = {
      id: runId,
      campaignId: campaign.id,
      startedAt,
      finishedAt: Date.now(),
      error: `search failed: ${e instanceof Error ? e.message : String(e)}`,
      dryRun,
      evaluations: [],
    }
    await appendAiCampaignRun(run)
    return { run }
  }

  if (hits.length === 0) {
    const run: AiCampaignRunRow = {
      id: runId,
      campaignId: campaign.id,
      startedAt,
      finishedAt: Date.now(),
      dryRun,
      evaluations: [],
      error: scoringMode === 'text'
        ? 'no posts matched the query'
        : 'no posts with images matched the query',
    }
    await appendAiCampaignRun(run)
    return { run }
  }

  let rawAgentText = ''
  let parseError: string | undefined
  const evaluations: AiCampaignRunRow['evaluations'] = []

  if (scoringMode === 'text') {
    // ── Text / poetry mode ────────────────────────────────────────────────
    const textPayload = JSON.stringify(
      { posts: hits.map((h) => ({ postUri: h.uri, text: h.text })) },
      null,
      2,
    )
    let parsed: TextScoringResult | null = null
    try {
      rawAgentText = await callAgent(agent, buildTextScoringPrompt(), textPayload)
      parsed = parseJsonish<TextScoringResult>(rawAgentText)
      if (!parsed || !Array.isArray(parsed.scores)) {
        parseError = 'agent reply did not contain `scores` array'
      }
    } catch (e) {
      parseError = e instanceof Error ? e.message : String(e)
    }

    const pqGate = campaign.scorePoetryGate ?? 3
    const pw = campaign.scorePoetryQualityWeight ?? 0.5
    const ow = 1 - pw

    const rawByUri = new Map<string, { poetryQuality: number; originality: number; reason: string }>()
    if (parsed && !parseError) {
      for (const s of parsed.scores) {
        if (typeof s?.postUri === 'string') {
          const pq = Math.max(0, Math.min(10, Number(s.poetryQuality) || 0))
          const o = Math.max(0, Math.min(10, Number(s.originality) || 0))
          rawByUri.set(s.postUri, {
            poetryQuality: pq,
            originality: o,
            reason: typeof s.reason === 'string' ? s.reason : '',
          })
        }
      }
    }

    for (const hit of hits) {
      const raw = rawByUri.get(hit.uri)
      const poetryQuality = raw?.poetryQuality ?? 0
      const originality = raw?.originality ?? 0
      const reason = raw?.reason ?? (parseError ?? 'no score returned')
      const score = !raw
        ? 0
        : poetryQuality <= pqGate
          ? poetryQuality
          : Math.min(10, Math.round((poetryQuality * pw + originality * ow) * 10) / 10)
      let action: 'none' | 'like' | 'repost' = 'none'
      if (score > campaign.repostThreshold) action = 'repost'
      else if (score >= campaign.likeMin && score <= campaign.likeMax) action = 'like'
      evaluations.push({
        postUri: hit.uri,
        authorHandle: hit.authorHandle,
        imageUrl: hit.images[0]?.fullsize ?? '',
        score,
        poetryQuality,
        originality,
        reason,
        action,
      })
    }
  } else {
    // ── Image / art mode (default) ────────────────────────────────────────
    const payload = JSON.stringify(
      {
        postUris: hits.map((h) => h.uri),
        hint: 'See the images attached in the same order.',
      },
      null,
      2,
    )
    let parsed: ScoringResult | null = null
    try {
      rawAgentText = await callAgent(
        agent,
        buildScoringPrompt(campaign.scorePhotoGate ?? true),
        payload,
        hits.map((h) => ({ url: h.images[0].fullsize })),
      )
      parsed = parseJsonish<ScoringResult>(rawAgentText)
      if (!parsed || !Array.isArray(parsed.scores)) {
        parseError = 'agent reply did not contain `scores` array'
      }
    } catch (e) {
      parseError = e instanceof Error ? e.message : String(e)
    }

    const qGate = campaign.scoreQualityGate ?? 3
    const qw = campaign.scoreQualityWeight ?? 0.45
    const lw = 1 - qw

    const rawByUri = new Map<string, { quality: number; lewdness: number; reason: string }>()
    if (parsed && !parseError) {
      for (const s of parsed.scores) {
        if (typeof s?.postUri === 'string') {
          const q = Math.max(0, Math.min(10, Number(s.quality) || 0))
          const l = Math.max(0, Math.min(10, Number(s.lewdness) || 0))
          rawByUri.set(s.postUri, {
            quality: q,
            lewdness: l,
            reason: typeof s.reason === 'string' ? s.reason : '',
          })
        }
      }
    }

    for (const hit of hits) {
      const raw = rawByUri.get(hit.uri)
      const quality = raw?.quality ?? 0
      const lewdness = raw?.lewdness ?? 0
      const reason = raw?.reason ?? (parseError ?? 'no score returned')
      const score = !raw
        ? 0
        : quality <= qGate
          ? quality
          : Math.min(10, Math.round((quality * qw + lewdness * lw) * 10) / 10)
      let action: 'none' | 'like' | 'repost' = 'none'
      if (score > campaign.repostThreshold) action = 'repost'
      else if (score >= campaign.likeMin && score <= campaign.likeMax) action = 'like'
      evaluations.push({
        postUri: hit.uri,
        authorHandle: hit.authorHandle,
        imageUrl: hit.images[0].fullsize,
        score,
        quality,
        lewdness,
        reason,
        action,
      })
    }
  }

  // Execute (unless dry run or parse failure).
  // repost → full engagement: repost + like + follow author.
  // like   → light engagement: just like.
  // Random 1.5–4.5 s delay between each individual API call.
  if (!dryRun && !parseError) {
    const sleepMinMs = (campaign.actionSleepMin ?? 1) * 1000
    const sleepMaxMs = (campaign.actionSleepMax ?? ((campaign.actionSleepMin ?? 1) + (campaign.actionSleepJitter ?? 3))) * 1000
    const randMs = () => sleepMinMs + Math.floor(Math.random() * Math.max(0, sleepMaxMs - sleepMinMs))

    for (const ev of evaluations) {
      if (ev.action === 'none') continue
      const hit = hits.find((h) => h.uri === ev.postUri)
      if (!hit) continue
      try {
        if (ev.action === 'repost') {
          // 1. Repost
          await withFreshAccess(account.id, () =>
            api.xrpc({
              accountId: account.id,
              path: 'com.atproto.repo.createRecord',
              body: {
                repo: account.did,
                collection: 'app.bsky.feed.repost',
                record: {
                  $type: 'app.bsky.feed.repost',
                  subject: { uri: hit.uri, cid: hit.cid },
                  createdAt: new Date().toISOString(),
                },
              },
            }),
          )
          await new Promise((r) => setTimeout(r, randMs()))

          // 2. Like
          await withFreshAccess(account.id, () =>
            api.xrpc({
              accountId: account.id,
              path: 'com.atproto.repo.createRecord',
              body: {
                repo: account.did,
                collection: 'app.bsky.feed.like',
                record: {
                  $type: 'app.bsky.feed.like',
                  subject: { uri: hit.uri, cid: hit.cid },
                  createdAt: new Date().toISOString(),
                },
              },
            }),
          )
          await new Promise((r) => setTimeout(r, randMs()))

          // 3. Follow author
          await withFreshAccess(account.id, () =>
            api.xrpc({
              accountId: account.id,
              path: 'com.atproto.repo.createRecord',
              body: {
                repo: account.did,
                collection: 'app.bsky.graph.follow',
                record: {
                  $type: 'app.bsky.graph.follow',
                  subject: hit.authorDid,
                  createdAt: new Date().toISOString(),
                },
              },
            }),
          )
          await new Promise((r) => setTimeout(r, randMs()))
        } else {
          // Like only
          await withFreshAccess(account.id, () =>
            api.xrpc({
              accountId: account.id,
              path: 'com.atproto.repo.createRecord',
              body: {
                repo: account.did,
                collection: 'app.bsky.feed.like',
                record: {
                  $type: 'app.bsky.feed.like',
                  subject: { uri: hit.uri, cid: hit.cid },
                  createdAt: new Date().toISOString(),
                },
              },
            }),
          )
          await new Promise((r) => setTimeout(r, randMs()))
        }
        ev.executed = true
      } catch (e) {
        ev.executed = false
        ev.executeError = e instanceof Error ? e.message : String(e)
      }
    }
  }

  const run: AiCampaignRunRow = {
    id: runId,
    campaignId: campaign.id,
    startedAt,
    finishedAt: Date.now(),
    dryRun,
    evaluations,
    error: parseError,
    rawAgentText: parseError ? rawAgentText : undefined,
    dedupStats: { seenUrisBefore, rawImageHits, passedDedup, handleFiltered },
  }
  await appendAiCampaignRun(run)
  return { run, parseError }
}

/** Compute the next scheduled tick using min/max interval (minutes).
 *  Falls back to legacy intervalMin + jitterMin for old campaigns.
 *  If runHourStart/runHourEnd are set, advances the result into the
 *  allowed window (same-day window only, e.g. 9–21). */
export function nextRunAt(campaign: AiCampaignRow, now = Date.now()): number {
  const minMs = (campaign.runIntervalMin ?? campaign.intervalMin) * 60_000
  const maxMs = (campaign.runIntervalMax ?? (campaign.intervalMin + campaign.jitterMin / 60)) * 60_000
  const rangeMs = Math.max(0, maxMs - minMs)
  let ts = now + minMs + Math.floor(Math.random() * rangeMs)
  const hs = campaign.runHourStart
  const he = campaign.runHourEnd
  if (hs != null && he != null && hs <= he) {
    const d = new Date(ts)
    const h = d.getHours()
    if (h < hs) {
      d.setHours(hs, Math.floor(Math.random() * 60), 0, 0)
      ts = d.getTime()
    } else if (h > he) {
      d.setDate(d.getDate() + 1)
      d.setHours(hs, Math.floor(Math.random() * 60), 0, 0)
      ts = d.getTime()
    }
  }
  return ts
}
